import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import AssetImage from '../../AssetImage';
import {
  Box as CropBox,
  BoxHandle,
  cornerOffset,
  inscribeRatio,
  SnapGuides,
  useCropCanvas,
} from './cropAdjustBoxes';
import SnapGuideLines from './SnapGuideLines';

type Source = { url: string; width: number; height: number };
const HANDLES: Exclude<BoxHandle, 'move'>[] = ['nw', 'ne', 'sw', 'se'];
const PRIMARY = 'primary.main';
/** Cards display each image `object-fit: cover` into a 16:9 frame. */
const CARD_FRAME_RATIO = 16 / 9;

type GrabFactory = (handle: BoxHandle) => (event: React.PointerEvent) => void;

/** Dashed marker for the centred 16:9 region of `box` that a card actually shows. */
function CropVisibleGuide({ box, scale }: { box: CropBox; scale: number }) {
  const abs = inscribeRatio(box, CARD_FRAME_RATIO);
  return (
    <Box
      aria-hidden
      data-testid="crop-visible-guide"
      sx={{
        position: 'absolute',
        pointerEvents: 'none',
        border: '1px dashed',
        borderColor: 'common.white',
        boxShadow: '0 0 0 1px rgba(0,0,0,0.45)',
        left: (abs.left - box.left) * scale,
        top: (abs.top - box.top) * scale,
        width: abs.width * scale,
        height: abs.height * scale,
      }}
    />
  );
}

/** One draggable / resizable crop box drawn over the split source. */
function CropAdjustBox({
  box,
  scale,
  label,
  locked,
  onGrab,
}: {
  box: CropBox;
  scale: number;
  label: string;
  locked: boolean;
  onGrab: GrabFactory;
}) {
  return (
    <Box
      onPointerDown={onGrab('move')}
      sx={{
        position: 'absolute',
        cursor: 'move',
        border: '2px solid',
        borderColor: PRIMARY,
        bgcolor: 'rgba(25,118,210,0.12)',
        boxSizing: 'border-box',
        left: box.left * scale,
        top: box.top * scale,
        width: box.width * scale,
        height: box.height * scale,
      }}
    >
      {!locked && <CropVisibleGuide box={box} scale={scale} />}
      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          top: 2,
          left: 4,
          bgcolor: PRIMARY,
          color: 'primary.contrastText',
          px: 0.5,
          borderRadius: 0.5,
          lineHeight: 1.5,
          maxWidth: 'calc(100% - 8px)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
      {HANDLES.map((h) => (
        <Box
          key={h}
          onPointerDown={onGrab(h)}
          sx={{ position: 'absolute', width: 12, height: 12, bgcolor: PRIMARY, ...cornerOffset[h] }}
        />
      ))}
    </Box>
  );
}

/** The source image with one crop box overlaid per card. */
function CropCanvas({
  source,
  boxes,
  headings,
  scale,
  locked,
  guides,
  wrapRef,
  onImageLoad,
  begin,
}: {
  source: Source;
  boxes: CropBox[];
  headings: string[];
  scale: number;
  locked: boolean;
  guides: SnapGuides;
  wrapRef: React.RefObject<HTMLDivElement>;
  onImageLoad: () => void;
  begin: (index: number, handle: BoxHandle) => (event: React.PointerEvent) => void;
}) {
  return (
    <Box
      ref={wrapRef}
      sx={{ position: 'relative', width: '100%', lineHeight: 0, userSelect: 'none' }}
    >
      <AssetImage
        url={source.url}
        alt="split source"
        onLoad={onImageLoad}
        sx={{ width: '100%', display: 'block' }}
      />
      {boxes.map((b, i) => (
        <CropAdjustBox
          key={i}
          box={b}
          scale={scale}
          locked={locked}
          label={headings[i] || `Image ${i + 1}`}
          onGrab={(handle) => begin(i, handle)}
        />
      ))}
      <SnapGuideLines guides={guides} scale={scale} />
    </Box>
  );
}

/** A hero image shows its whole crop, so there is no 16:9 frame to guide or lock to. */
function HeroCropInstructions() {
  return (
    <DialogContentText sx={{ mb: 1 }}>
      Drag the box to move it, or a corner to resize. Everything inside the box is the image shown
      on the page.
    </DialogContentText>
  );
}

function CropAdjustInstructions({
  locked,
  onLockedChange,
}: {
  locked: boolean;
  onLockedChange: (locked: boolean) => void;
}) {
  return (
    <>
      <DialogContentText sx={{ mb: 1 }}>
        {locked
          ? 'Each box is locked to the card’s 16:9 shape — move or resize it and everything inside the box shows on the card.'
          : 'Drag a box to move it, or a corner to resize. Crop to any shape you like — the card shows each image centred in a wide 16:9 frame, so the dashed area is what will be visible; the rest is kept but hidden on the card.'}
      </DialogContentText>
      <FormControlLabel
        sx={{ mb: 0.5 }}
        control={
          <Checkbox
            size="small"
            checked={locked}
            onChange={(e) => onLockedChange(e.target.checked)}
          />
        }
        label="Lock boxes to the card’s 16:9 shape"
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Boxes snap into place against the edges and each other. To move a box straight up/down or
        straight left/right without it drifting, hold Shift while you drag it.
      </Typography>
    </>
  );
}

function CropAdjustActions({
  busy,
  applyLabel,
  onReset,
  onCancel,
  onApply,
}: {
  busy: boolean;
  applyLabel: string;
  onReset: () => void;
  onCancel: () => void;
  onApply: () => void;
}) {
  return (
    <DialogActions>
      <Button onClick={onReset} color="inherit" disabled={busy} sx={{ mr: 'auto' }}>
        Reset
      </Button>
      <Button onClick={onCancel} color="inherit" disabled={busy}>
        Cancel
      </Button>
      <Button
        onClick={onApply}
        variant="contained"
        disabled={busy}
        startIcon={busy ? <CircularProgress size={14} /> : undefined}
      >
        {applyLabel}
      </Button>
    </DialogActions>
  );
}

/**
 * Drag / resize a box per card over the retained split source, then re-crop.
 */
export default function CropAdjustDialog({
  open,
  source,
  initialBoxes,
  headings,
  busy,
  onApply,
  onCancel,
  variant = 'cards',
}: {
  open: boolean;
  source: Source | null;
  initialBoxes: CropBox[];
  headings: string[];
  busy: boolean;
  onApply: (boxes: CropBox[]) => void;
  onCancel: () => void;
  /** `hero`: one box whose whole area is shown, so no 16:9 guide or lock. */
  variant?: 'cards' | 'hero';
}) {
  const hero = variant === 'hero';
  const [locked, setLocked] = useState(false);
  const { wrapRef, scale, boxes, begin, measure, guides, resetBoxes } = useCropCanvas(
    source,
    initialBoxes,
    locked ? CARD_FRAME_RATIO : undefined,
  );

  if (!source) return null;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="lg"
      fullWidth
      aria-labelledby="crop-adjust-title"
    >
      <DialogTitle id="crop-adjust-title">
        {hero ? 'Adjust the image crop' : 'Adjust the image crops'}
      </DialogTitle>
      <DialogContent>
        {hero ? (
          <HeroCropInstructions />
        ) : (
          <CropAdjustInstructions locked={locked} onLockedChange={setLocked} />
        )}
        <CropCanvas
          source={source}
          boxes={boxes}
          headings={headings}
          scale={scale}
          locked={locked || hero}
          guides={guides}
          wrapRef={wrapRef}
          onImageLoad={measure}
          begin={begin}
        />
      </DialogContent>
      <CropAdjustActions
        busy={busy}
        applyLabel={hero ? 'Apply crop' : 'Apply crops'}
        onReset={resetBoxes}
        onCancel={onCancel}
        onApply={() => onApply(boxes)}
      />
    </Dialog>
  );
}
